import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import Workout from '../models/Workout';
import GlobalWorkout from '../models/GlobalWorkout';
import WorkoutSession from '../models/WorkoutSession';
import EMOMTimer from '../components/timers/EMOMTimer';
import AMRAPTimer from '../components/timers/AMRAPTimer';
import ForTimeTimer from '../components/timers/ForTimeTimer';
import TabataTimer from '../components/timers/TabataTimer';
import { resolveTimerSettings } from '../config/workoutCategories';

const WorkoutSessionPage = () => {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timerStarted, setTimerStarted] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [finalTime, setFinalTime] = useState(null);

  // Editable timer settings
  const [rounds, setRounds] = useState(null);
  const [intervalSec, setIntervalSec] = useState(null);
  const [capSec, setCapSec] = useState(null);
  const [workSec, setWorkSec] = useState(null);
  const [restSec, setRestSec] = useState(null);

  useEffect(() => {
    if (user && workoutId) {
      loadWorkout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId, user]);

  const loadWorkout = async () => {
    try {
      setLoading(true);

      // Try user workout first
      let workoutData = await Workout.getById(user.uid, workoutId);

      // If not found, try global workout
      if (!workoutData) {
        console.log('User workout not found, checking global workouts...');
        workoutData = await GlobalWorkout.getById(workoutId);

        if (workoutData) {
          console.log('Found global workout:', workoutData.name);
          try {
            const newWorkoutId = await Workout.copyFromGlobal(user.uid, workoutId, workoutData);
            console.log('Copied to user workout with ID:', newWorkoutId);
            workoutData = await Workout.getById(user.uid, newWorkoutId);
            if (workoutData) {
              workoutData.id = newWorkoutId;
            }
          } catch (copyErr) {
            console.error('Error copying global workout:', copyErr);
            workoutData.id = workoutId;
          }
        }
      }

      if (workoutData) {
        console.log('Loaded workout:', workoutData);
        setWorkout(workoutData);

        // Merge document values with category defaults
        const timer = resolveTimerSettings(workoutData);
        if (timer.rounds != null) setRounds(timer.rounds);
        if (timer.intervalSec != null) setIntervalSec(timer.intervalSec);
        if (timer.capSec != null) setCapSec(timer.capSec);
        if (timer.workSec != null) setWorkSec(timer.workSec);
        if (timer.restSec != null) setRestSec(timer.restSec);

        // Auto-start timer immediately
        setTimeout(() => {
          handleStartTimer();
        }, 100);
      } else {
        console.error('Workout not found:', workoutId);
        alert('Workout not found');
        navigate('/workouts');
      }
    } catch (err) {
      console.error('Error loading workout:', err);
      alert(`Failed to load workout: ${err.message}`);
      navigate('/workouts');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTimer = () => {
    setTimerStarted(true);
  };

  const handleTimerComplete = async (timeElapsed = null) => {
    setSessionComplete(true);

    if (timeElapsed !== null) {
      setFinalTime(timeElapsed);
    }

    try {
      let value = 0;

      if (workout.scoreType === 'time' && timeElapsed) {
        value = timeElapsed;
      }

      if (value > 0) {
        await WorkoutSession.save({
          userId: user.uid,
          workoutId: workout.id,
          value: value,
          performedAt: new Date(),
          notes: ''
        });
      }
    } catch (err) {
      console.error('Error saving session:', err);
    }
  };

  const handleTimerStop = () => {
    navigate(`/workout/${workout.id}`);
  };

  const handleSaveAndExit = () => {
    navigate(`/workout/${workout.id}`);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center text-gray-500 dark:text-gray-400">
          Loading workout...
        </div>
      </div>
    );
  }

  if (!workout) {
    return null;
  }

  // Timer is running
  if (timerStarted && !sessionComplete) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">

        {workout.category === 'EMOM' && (
          <EMOMTimer
            rounds={rounds}
            intervalSec={intervalSec}
            exercises={workout.exercises || [workout.name]}
            onComplete={handleTimerComplete}
            onStop={handleTimerStop}
          />
        )}

        {workout.category === 'AMRAP' && (
          <AMRAPTimer
            capSec={capSec}
            exercises={workout.exercises || [workout.name]}
            onComplete={handleTimerComplete}
            onStop={handleTimerStop}
          />
        )}

        {workout.category === 'ForTime' && (
          <ForTimeTimer
            capSec={capSec}
            exercises={workout.exercises || [workout.name]}
            onComplete={handleTimerComplete}
            onStop={handleTimerStop}
          />
        )}

        {workout.category === 'HIIT' && (
          <TabataTimer
            workSec={workSec}
            restSec={restSec}
            rounds={rounds}
            exercises={workout.exercises || [workout.name]}
            onComplete={handleTimerComplete}
            onStop={handleTimerStop}
          />
        )}

        {/* Fallback for other categories */}
        {!['EMOM', 'AMRAP', 'ForTime', 'HIIT'].includes(workout.category) && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Timer not available for this workout type
            </p>
            <button
              onClick={handleTimerStop}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 rounded-lg"
            >
              Back to Workout
            </button>
          </div>
        )}
      </div>
    );
  }

  // Session complete
  if (sessionComplete) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Workout Complete!
          </h2>

          {finalTime && (
            <div className="mb-6">
              <p className="text-gray-600 dark:text-gray-400 mb-2">Final Time</p>
              <p className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
                {Math.floor(finalTime / 60)}:{String(finalTime % 60).padStart(2, '0')}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSaveAndExit}
              className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Workout Details
            </button>
            <button
              onClick={() => navigate('/workouts')}
              className="px-8 py-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
            >
              Back to Workouts
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pre-workout configuration view
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/workouts')}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Workouts
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {workout.name}
        </h1>
        <div className="flex items-center gap-2 mb-4">
          <span className="px-3 py-1 text-sm font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
            {workout.category}
          </span>
        </div>
        {workout.description && (
          <p className="text-gray-600 dark:text-gray-400">
            {workout.description}
          </p>
        )}
      </div>

      {/* Workout Exercises/Details */}
      {workout.exercises && Array.isArray(workout.exercises) && workout.exercises.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Exercises
          </h3>
          <ul className="space-y-2">
            {workout.exercises.map((exercise, index) => (
              <li key={index} className="flex items-start">
                <span className="text-indigo-600 dark:text-indigo-400 mr-2">•</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {typeof exercise === 'string' ? exercise : exercise.name || 'Exercise'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Timer Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Configure Timer
        </h3>

        {workout.category === 'EMOM' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rounds
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={rounds}
                onChange={(e) => setRounds(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interval (seconds)
              </label>
              <input
                type="number"
                min="10"
                max="300"
                value={intervalSec}
                onChange={(e) => setIntervalSec(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        )}

        {workout.category === 'AMRAP' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Cap (minutes)
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={capSec ? Math.floor(capSec / 60) : ''}
              onChange={(e) => setCapSec(parseInt(e.target.value) * 60)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        )}

        {workout.category === 'HIIT' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Work Time (seconds)
              </label>
              <input
                type="number"
                min="5"
                max="120"
                value={workSec}
                onChange={(e) => setWorkSec(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rest Time (seconds)
              </label>
              <input
                type="number"
                min="5"
                max="60"
                value={restSec}
                onChange={(e) => setRestSec(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rounds
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={rounds}
                onChange={(e) => setRounds(parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total time: {Math.floor(((workSec + restSec) * rounds) / 60)} min {((workSec + restSec) * rounds) % 60}s
            </div>
          </div>
        )}

        {workout.category === 'ForTime' && (
          <p className="text-gray-600 dark:text-gray-400">
            Stopwatch mode - Complete the workout as fast as possible
          </p>
        )}
      </div>

      {/* Start Button */}
      <button
        onClick={handleStartTimer}
        className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xl transition-colors shadow-lg"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
        </svg>
        Start Workout
      </button>
    </div>
  );
};

export default WorkoutSessionPage;
